import type * as React from "react";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { MenuItem } from "@mui/material";
import { withStyles } from "@mui/styles";
import { C_DROPDOWN_HOVER } from "../icon/Colors";
import { Select, type SelectProps } from "./Select";
import { toMapFn } from "../../../utils/reducers";
import Checkbox from "../Checkbox";
import { Button } from "../Button/Button";
import { useTranslation } from "react-i18next";
import { type ItemDef } from "~/common/types";

export interface SimpleMultiSelectProps extends Omit<SelectProps, "multiple" | "open" | "onOpen" | "onClose"> {
  values: ItemDef[];
  value: string[] | number[];
}

const SimpleMultiSelectItem = withStyles((theme) => ({
  selected: {
    backgroundColor: "transparent !important",
  },
  "&.Mui-focusVisible": {
    backgroundColor: `${C_DROPDOWN_HOVER} !important`,
  },
}))(MenuItem);

export const SimpleMultiSelect = (props: SimpleMultiSelectProps) => {
  const { values, children, onChange, value, ...rest } = props;
  const { t } = useTranslation();

  const [internalValue, setInternalValue] = useState(value);

  useEffect(() => setInternalValue(value), [value]);

  // Component does not provide a better type...
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const internalOnChange = useCallback((e: any) => setInternalValue(e.target.value), []);

  const [isOpen, setOpen] = useState(false);

  const elements = useMemo(() => {
    return createMultiselectItems(values, internalValue as string[], rest["data-test-id"]);
  }, [values, internalValue]);

  const defaultRenderValue = useMemo(() => {
    if (rest.renderValue) {
      return rest.renderValue;
    }
    const labelMap = toMapFn(
      values,
      (item) => item.code,
      (item) => item.label,
    );
    return (value: unknown) => {
      return (value as string[]).map((v) => labelMap[v]).join(", ") as ReactNode;
    };
  }, [values]);

  const onSave = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      // TODO - check after m-ui update
      // @ts-ignore
      onChange?.({ target: { value: [...internalValue], name: "" } }, null);
      setOpen(false);
    },
    [internalValue],
  );

  const onClose = useCallback(() => {
    setInternalValue(value);
    setOpen(false);
  }, [value]);

  const onOpen = useCallback(() => {
    setOpen(true);
  }, []);

  return (
    <Select
      {...rest}
      multiple={true}
      renderValue={rest.renderValue ?? defaultRenderValue}
      value={internalValue}
      onChange={internalOnChange}
      onClose={onClose}
      onOpen={onOpen}
      open={isOpen}
      native={false}
      variant={"outlined"}
    >
      {elements}
      <div className={"p-3"}>
        <Button onClick={onSave} fullWidth={true}>
          {t("generic.save")}
        </Button>
      </div>
      {children}
    </Select>
  );
};

export const createMultiselectItems = (items: ItemDef[], values: string[], testId?: string) => {
  const checkedMap = toMapFn(
    values,
    (key) => key,
    () => true,
  );
  return items.map((item, index) => {
    const childId = testId ? `${testId}-item` : undefined;
    const active = !!checkedMap[item.code];
    return (
      <SimpleMultiSelectItem
        key={item.code}
        className={"d-flex"}
        value={item.code}
        data-test-id={childId}
        disabled={item.disabled && !active}
      >
        <Checkbox checked={active} />
        <span className={"icon-margin-l"}>{item.label}</span>
      </SimpleMultiSelectItem>
    );
  });
};
